import { useMemo } from 'react';
import { Label } from 'react-bootstrap';
import { ColDef, IHeaderParams, SortChangedEvent, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { MatchStatus, PatientType } from '@nkr/enums/index';
import { IPreselectLogs } from '@nkr/models/donor-profile';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './style';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';

interface IProps {
    data: IPreselectLogs[],
    isAdmin?: boolean,
    isCenterView?: boolean
}

const Accepts = (props: IProps) => {
    if (props.data == null) {
        return null
    }
    const cols: ColDef[] = [
        {
            headerName: 'Rank',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: any) => parseInt(params.node.id) + 1,
            width: 50,
            maxWidth: 50
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 140,
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient {!props.isCenterView ? <br /> : null} Alias</SortableHeaderComponent>,
            width: 120,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPreselectLogs;
                let { recipientAlias, id, recipientCenterId } = data;

                let recipientAliasDisplay = recipientAlias;
                if (recipientAlias != undefined && recipientAlias.length > 13) {
                    recipientAliasDisplay = recipientAlias.substring(0, 13) + '...';
                }
                return (props.isAdmin && id > 0
                    ? <a target={'_blank'} href={"/center/" + recipientCenterId + "/recipient/" + id + "/profile/general"}>{recipientAliasDisplay}</a>
                    : <span>{recipientAliasDisplay}</span>)
            },
        },
        {
            headerName: 'Type',
            colId: 'recipientType',
            field: 'recipientType',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient {!props.isCenterView ? <br /> : null} Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPreselectLogs;
                let style = 'info';

                if (data.recipientTypeId == PatientType.KidneyReplacementCase) {
                    style = 'danger';
                }

                if (data.recipientTypeId == PatientType.Paired) {
                    style = 'default';
                }

                if (data.recipientTypeId == PatientType.Waitlist) {
                    style = 'warning';
                }

                if (data.recipientTypeId == PatientType.StandardVoucherHolder || data.recipientTypeId == PatientType.FamilyVoucherHolder) {
                    style = 'success';
                }

                return <Label bsStyle={style}>{data.recipientType}</Label>;
            }
        },
        {
            colId: 'recipientAge',
            field: 'recipientAge',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient <br /> Age</SortableHeaderComponent>,
            width: 80,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'recipientBloodType',
            field: 'recipientBloodType',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient <br /> ABO</SortableHeaderComponent>,
            width: 80,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'highestDeclineRatio',
            field: 'highestDeclineRatio',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor <br /> Decline Ratio</SortableHeaderComponent>,
            width: 100,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'forecastedWaitTime',
            field: 'forecastedWaitTime',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wait Time <br /> (Months)</SortableHeaderComponent>,
            width: 80,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'dialysisMonths',
            field: 'dialysisMonths',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Dialysis Time <br />(Months)</SortableHeaderComponent>,
            width: 100,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'roundedCpra4',
            field: 'roundedCpra4',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>NKR <br /> cPRA</SortableHeaderComponent>,
            width: 90,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'epletMm',
            field: 'epletMm',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet <br /> MM</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPreselectLogs;
                return (
                    <SingleEpletMmRisk data={data.epletMm} isAdmin={true} placement='bottom' />
                )
            },
            width: 90,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'donorPreselectDecision',
            field: 'donorPreselectDecision',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Preselect <br />Status</SortableHeaderComponent>,
            cellRendererFramework: (params) => {
                let accept = params.data as IPreselectLogs;
                return <td><Label bsStyle={accept.isAccepted == true ? 'success' : accept.isAccepted == false ? 'danger' : accept.isReEvalNeeded == true ? 'warning' : 'default'}>{accept.donorPreselectDecision}</Label></td>;
            },
            width: 125,
            hide: !props.isCenterView ? false : true
        },
        {
            colId: 'decisionDateTime',
            field: 'decisionDateTime',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Accepted</SortableHeaderComponent>,
            comparator: Utility.dateComparator,
            hide: props.isCenterView ? false : true
        },
        {
            colId: 'recipientIsActive',
            field: 'recipientIsActive',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Active?</SortableHeaderComponent>,
            cellRendererFramework: (params) => {
                let accept = params.data as IPreselectLogs;
                return <td><Label bsStyle={accept.recipientIsActive ? 'success' : 'warning'}>{accept.recipientIsActive ? 'Active' : 'Inactive'}</Label></td>;
            },
            hide: props.isCenterView ? false : true
        },
        {
            colId: 'recipientIsExportable',
            field: 'recipientIsExportable',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Exportable?</SortableHeaderComponent>,
            cellRendererFramework: (params) => {
                let accept = params.data as IPreselectLogs;
                return <td><Label bsStyle={accept.recipientIsExportable ? 'success' : 'warning'}>{accept.recipientIsExportable ? 'Yes' : 'No'}</Label></td>;
            },
            width: 65,
            hide: props.isCenterView ? false : true
        },
        {
            colId: 'recipientStatus',
            field: 'recipientStatus',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            cellRendererFramework: (params) => {
                let accept = params.data as IPreselectLogs;
                return <td><Label bsStyle={accept.recipientStatusId == MatchStatus.NeedsMatch ? 'success' : 'warning'}>{accept.recipientStatus}</Label></td>;
            },
            hide: props.isCenterView ? false : true
        },
    ]

    const data = useMemo(() => props.data, [props.data])
    const colDef = useMemo(() => cols, [cols])

    return (
        <>
            <style>{`
        #grid-wrapper .ag-header {
            background-color: #dee8f2;
        }
        #grid-wrapper .ag-header-cell {
            color: black;
            font-weight: normal;
        }
        #grid-wrapper .ag-header-cell-label {
            color: black;
            font-weight: normal;
        }
    `}</style>
            <GridWrapper
                id='grid-wrapper'
                className='ag-theme-bootstrap'
                style={{
                    width: '100%',
                    height: data.length >= 20 ? '40vh' : undefined,
                }}
            >
                <AgGridReact
                    defaultColDef={{
                        headerClass: 'list-header',
                        cellClass: 'list-cell',
                        sortable: true,
                        resizable: true,
                        ...(props.isCenterView ? { flex: 1 } : {})
                    }}
                    columnDefs={colDef}
                    rowData={data}
                    headerHeight={props.isCenterView ? 32 : 45}
                    suppressCellSelection={true}
                    suppressContextMenu={true}
                    enableCellTextSelection={true}
                    domLayout={data.length < 20 ? 'autoHeight' : null}
                    gridOptions={{ multiSortKey: 'ctrl' }}
                    onGridReady={(e: GridReadyEvent) => e.api.sizeColumnsToFit()}
                    onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
                />
            </GridWrapper>
        </>
    );
}

export default Accepts;